// File: @/lib/analytics/events.ts

/**
 * Typed event creator functions — one per AnalyticsEvent variant.
 *
 * Why these exist:
 * - Type-safe alternative to ad-hoc plain objects
 * - Ensures required fields are always present
 * - Single place to change an event's shape
 * - Version number is automatically applied
 *
 * Usage:
 *   import { track } from '@/lib/analytics';
 *   import { scrollDepthEvent } from '@/lib/analytics/events';
 *
 *   track(scrollDepthEvent(50, 'homepage', 'landing'));
 */

import type {
  ClientErrorEvent,
  ContentCompletionEvent,
  CTAClickEvent,
  ExternalLinkClickEvent,
  QualifiedVisitEvent,
  ReadTimeEvent,
  ScrollDepthEvent,
  SocialClickEvent,
} from './types';

// ===================================
// ENGAGEMENT CREATORS
// ===================================

export function scrollDepthEvent(
  percent: ScrollDepthEvent['percent'],
  contentId: string,
  category: string,
): ScrollDepthEvent {
  return { name: 'engagement.scroll_depth', version: 1, percent, content_id: contentId, category };
}

export function qualifiedVisitEvent(
  engagementTimeMs: number,
  contentId: string,
  category: string,
): QualifiedVisitEvent {
  return {
    name: 'engagement.qualified_visit',
    version: 1,
    engagement_time_ms: engagementTimeMs,
    content_id: contentId,
    category,
  };
}

export function contentCompletionEvent(
  contentType: string,
  contentId: string,
  category: string,
): ContentCompletionEvent {
  return {
    name: 'engagement.content_completion',
    version: 1,
    content_type: contentType,
    content_id: contentId,
    category,
  };
}

export function readTimeEvent(
  readTimeS: number,
  contentId: string,
  category: string,
): ReadTimeEvent {
  return {
    name: 'engagement.read_time',
    version: 1,
    read_time_s: readTimeS,
    content_id: contentId,
    category,
  };
}

// ===================================
// INTERACTION CREATORS
// ===================================

export function externalLinkClickEvent(
  destinationUrl: string,
  linkText: string,
  placement: string,
): ExternalLinkClickEvent {
  return {
    name: 'interaction.external_link_click',
    version: 1,
    destination_url: destinationUrl,
    link_text: linkText,
    placement,
  };
}

export function socialClickEvent(
  network: string,
  placement: SocialClickEvent['placement'],
): SocialClickEvent {
  return { name: 'interaction.social_click', version: 1, network, placement };
}

export function ctaClickEvent(
  elementId: string,
  elementText: string,
  placement: string,
): CTAClickEvent {
  return {
    name: 'interaction.cta_click',
    version: 1,
    element_id: elementId,
    element_text: elementText,
    placement,
  };
}

// ===================================
// RELIABILITY CREATORS
// ===================================

export function clientErrorEvent(
  errorName: string,
  errorMessage: string,
  componentStack?: string,
): ClientErrorEvent {
  return {
    name: 'app.client_error',
    version: 1,
    error_name: errorName,
    error_message: errorMessage,
    ...(componentStack !== undefined ? { component_stack: componentStack } : {}),
  };
}
